import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/App";
import axios from "axios";
import { Lock, ArrowLeft, CheckCircle } from "lucide-react";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset failed. The link may have expired.");
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-md">
          <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-xl font-heading font-bold mb-2">Invalid Reset Link</h1>
          <p className="text-muted-foreground mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/">
            <Button className="rounded-full"><ArrowLeft className="mr-2 h-4 w-4" /> Back to Login</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-md">
          <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
          <h1 className="text-xl font-heading font-bold mb-2">Password Reset!</h1>
          <p className="text-muted-foreground mb-6">Your password has been updated. You can now sign in with your new password.</p>
          <Link to="/">
            <Button className="rounded-full" data-testid="back-to-login-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/thrively-logo.png" alt="Thrively" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold">Create New Password</h1>
          <p className="text-muted-foreground mt-1">Enter your new password below</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 bg-white rounded-3xl shadow-lg p-8">
          <div className="space-y-2">
            <Label>New Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              className="rounded-xl"
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Confirm Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              className="rounded-xl"
              data-testid="confirm-password-input"
            />
          </div>
          <Button type="submit" className="w-full rounded-full h-12 mt-4" disabled={loading} data-testid="reset-submit-btn">
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
        </form>
        <div className="text-center mt-4">
          <Link to="/" className="text-sm text-primary hover:underline">Back to Sign In</Link>
        </div>
      </div>
    </div>
  );
}
